import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  collection,
  getDocs,
  getFirestore,
  orderBy,
  query,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from "@/plugins/firebase";

const db = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp, "europe-west2");

export const useAnnouncementStore = defineStore("announcements", () => {
  const items = ref([]);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref("");

  const sortAnnouncements = (list) =>
    [...list].sort((a, b) => {
      const aOrder = Number.isFinite(a?.order) ? a.order : 0;
      const bOrder = Number.isFinite(b?.order) ? b.order : 0;
      if (aOrder === bOrder) {
        return (a.title || "").localeCompare(b.title || "");
      }
      return aOrder - bOrder;
    });

  const sortedAnnouncements = computed(() => sortAnnouncements(items.value));

  const fetchAll = async (force = false) => {
    if (loading.value) return;
    if (loaded.value && !force) return;

    loading.value = true;
    error.value = "";

    try {
      const snapshot = await getDocs(
        query(collection(db, "announcements"), orderBy("order", "asc")),
      );
      const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      items.value = docs;
      loaded.value = true;
    } catch (err) {
      console.error("Failed to fetch announcements", err);
      error.value = err?.message || "Failed to fetch announcements";
    } finally {
      loading.value = false;
    }
  };

  const refresh = async () => fetchAll(true);

  const activeForBankComputed = computed(
    () => (bankId) =>
      sortedAnnouncements.value.filter((announcement) => {
        const banks = Array.isArray(announcement?.banks)
          ? announcement.banks
          : [];
        return (
          announcement.isActive !== false &&
          (banks.length === 0 || !bankId || banks.includes(bankId))
        );
      }),
  );

  const getActiveForBank = (bankId) => activeForBankComputed.value(bankId);

  const upsertLocal = (announcement) => {
    const idx = items.value.findIndex((item) => item.id === announcement.id);
    if (idx === -1) {
      items.value.push(announcement);
    } else {
      items.value[idx] = announcement;
    }
  };

  const removeLocal = (id) => {
    items.value = items.value.filter((item) => item.id !== id);
  };

  const saveAnnouncement = async (announcement) => {
    const callable = httpsCallable(functions, "adminSaveAnnouncement");
    const { data } = await callable({ announcement });
    const saved = data?.announcement;
    if (saved) {
      upsertLocal(saved);
    }
    return saved;
  };

  const deleteAnnouncement = async (id) => {
    const callable = httpsCallable(functions, "adminDeleteAnnouncement");
    await callable({ id });
    removeLocal(id);
  };

  const getAnnouncementById = (id) =>
    items.value.find((announcement) => announcement.id === id) || null;

  return {
    items,
    loading,
    loaded,
    error,
    sortedAnnouncements,
    fetchAll,
    refresh,
    getActiveForBank,
    activeForBankComputed,
    getAnnouncementById,
    saveAnnouncement,
    deleteAnnouncement,
  };
});
